import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Calendar, Wifi, IdCard, Cpu, Grid3x3, Droplet, Timer, RotateCcw, Trash2, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import type { DeviceStatus } from '@/lib/types';

import { ScheduleSection }    from './ScheduleSection';
import { WifiSection }        from './WifiSection';
import { IdentitySection }    from './IdentitySection';
import { ModulesSection }     from './ModulesSection';
import { RelayMapSection }    from './RelayMapSection';
import { FlowHwSection }      from './FlowHwSection';
import { CycleTimingSection } from './CycleTimingSection';
import { RebootSection }      from './RebootSection';
import { FactoryResetSection } from './FactoryResetSection';
import { DeleteDeviceSection } from './DeleteDeviceSection';

interface Props {
  open: boolean;
  onClose: () => void;
  device: DeviceStatus;
  gateDisabled: boolean;
  isSuperadmin: boolean;
}

type SectionId =
  | 'schedule' | 'wifi' | 'identity' | 'modules'
  | 'relay-map' | 'flow-hw' | 'cycle-timing'
  | 'reboot' | 'factory-reset' | 'delete-device';

// `open` marks the sections non-superadmin roles may use (Schedule + Wi-Fi).
// Everything else is superadmin-only and hidden, not just disabled.
const sections: Array<{ id: SectionId; label: string; icon: React.ComponentType<{ className?: string }>; group: 'operations' | 'commissioning' | 'danger'; open?: boolean }> = [
  { id: 'schedule',     label: 'Schedule',      icon: Calendar, group: 'operations', open: true },
  { id: 'wifi',         label: 'Wi-Fi',         icon: Wifi,     group: 'operations', open: true },
  { id: 'identity',     label: 'Identity',      icon: IdCard,   group: 'commissioning' },
  { id: 'modules',      label: 'Modbus modules', icon: Cpu,     group: 'commissioning' },
  { id: 'relay-map',    label: 'Relay map',     icon: Grid3x3,  group: 'commissioning' },
  { id: 'flow-hw',      label: 'Flow sensor',   icon: Droplet,  group: 'commissioning' },
  { id: 'cycle-timing', label: 'Cycle timing',  icon: Timer,    group: 'commissioning' },
  { id: 'reboot',       label: 'Reboot',        icon: RotateCcw, group: 'danger' },
  { id: 'factory-reset', label: 'Factory reset', icon: Trash2,   group: 'danger' },
  { id: 'delete-device', label: 'Delete device', icon: XCircle,  group: 'danger' },
];

export function SettingsDrawer({ open, onClose, device, gateDisabled, isSuperadmin }: Props) {
  const [active, setActive] = useState<SectionId>('schedule');

  // Superadmin sees every section; other roles only Schedule + Wi-Fi. The
  // server enforces the same split per endpoint (routes/control.js).
  const visible = isSuperadmin ? sections : sections.filter((s) => s.open);
  const current: SectionId = visible.some((s) => s.id === active) ? active : visible[0].id;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-40 bg-black/40"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.div
                initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 240 }}
                className="fixed right-0 top-0 z-50 h-full w-full sm:max-w-[720px] bg-white shadow-2xl flex flex-col"
              >
                <header className="flex items-center justify-between px-5 h-14 border-b border-slate-200 shrink-0">
                  <div className="min-w-0">
                    <Dialog.Title className="font-bold text-ink text-base truncate">
                      Device settings
                    </Dialog.Title>
                    <p className="text-xs text-slate-500 font-mono truncate">{device.client_id}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                    <X className="w-4 h-4" />
                  </Button>
                </header>

                <div className="flex-1 flex flex-col sm:flex-row min-h-0">
                  <nav className="sm:w-56 sm:border-r border-slate-200 sm:overflow-y-auto bg-slate-50">
                    <ul className="flex sm:flex-col overflow-x-auto sm:overflow-x-visible p-2 gap-1 sm:gap-0.5">
                      {(['operations', 'commissioning', 'danger'] as const).map((group) => (
                        <li key={group} className="sm:contents">
                          {visible.filter((s) => s.group === group).map((s) => {
                            const Icon = s.icon;
                            const isActive = current === s.id;
                            return (
                              <button
                                key={s.id}
                                onClick={() => setActive(s.id)}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                                  'sm:w-full sm:justify-start',
                                  isActive
                                    ? 'bg-ink text-white'
                                    : 'text-slate-600 hover:bg-white hover:text-ink',
                                  s.group === 'danger' && !isActive && 'text-red-600',
                                )}
                              >
                                <Icon className="w-4 h-4 shrink-0" />
                                {s.label}
                              </button>
                            );
                          })}
                        </li>
                      ))}
                    </ul>
                  </nav>

                  <div className="flex-1 overflow-y-auto p-5">
                    {current === 'schedule'      && <ScheduleSection      device={device} disabled={gateDisabled} />}
                    {current === 'wifi'          && <WifiSection          device={device} disabled={gateDisabled} />}
                    {current === 'identity'      && <IdentitySection      device={device} disabled={gateDisabled} />}
                    {current === 'modules'       && <ModulesSection       device={device} disabled={gateDisabled} />}
                    {current === 'relay-map'     && <RelayMapSection      device={device} disabled={gateDisabled} />}
                    {current === 'flow-hw'       && <FlowHwSection        device={device} disabled={gateDisabled} />}
                    {current === 'cycle-timing'  && <CycleTimingSection   device={device} disabled={gateDisabled} />}
                    {current === 'reboot'        && <RebootSection        device={device} disabled={gateDisabled} />}
                    {current === 'factory-reset' && <FactoryResetSection  device={device} disabled={gateDisabled} />}
                    {current === 'delete-device' && <DeleteDeviceSection  device={device} />}
                  </div>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
